import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { BarChart3, Users, LogOut, Map, Layers, FileText, TrendingUp, ShieldCheck, Bell, ArrowUpRight, ClipboardList } from "lucide-react";
import { useCyclePicker, CyclePickerBar } from "../hooks/useCyclePicker";

const NAV_ITEMS = [
    { to: "/enterprise", icon: BarChart3, label: "Command Center" },
    { to: "/enterprise/heatmap", icon: Map, label: "Workforce Heatmap" },
    { to: "/enterprise/assessments", icon: ClipboardList, label: "Assessments" },
    { to: "/enterprise/cohorts", icon: Layers, label: "Cohorts" },
    { to: "/enterprise/report", icon: FileText, label: "Board Report" },
];

export const EnterpriseSidebar = ({ current }) => {
    const { user, logout, api } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [showNotifs, setShowNotifs] = useState(false);

    useEffect(() => {
        api.get("/v2/notifications").then((r) => setNotifications(r.data.notifications || [])).catch(() => {});
    }, [api]);

    const unread = notifications.filter((n) => !n.read).length;

    return (
        <aside className="w-56 min-h-screen bg-card border-r border-border/40 flex flex-col fixed left-0 top-0" data-testid="enterprise-sidebar">
            <div className="p-5 border-b border-border/40">
                <Link to="/" className="text-lg font-bold text-white font-body">Realloc</Link>
                <p className="text-xs text-gray-600 mt-0.5">Enterprise</p>
            </div>
            <nav className="flex-1 py-4 space-y-1 px-2">
                {NAV_ITEMS.map((item) => (
                    <Link key={item.to} to={item.to}
                        className={`flex items-center gap-3 px-3 py-2 text-sm rounded transition-colors ${current === item.to ? "bg-white/10 text-white" : "text-gray-500 hover:text-white hover:bg-white/5"}`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <item.icon className="w-4 h-4" />
                        {item.label}
                    </Link>
                ))}
            </nav>
            <div className="p-4 border-t border-border/40 space-y-3">
                <div className="relative">
                    <button onClick={() => setShowNotifs(!showNotifs)} className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors w-full" data-testid="notification-bell">
                        <Bell className="w-4 h-4" />
                        Notifications
                        {unread > 0 && <span className="ml-auto bg-white text-black text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">{unread}</span>}
                    </button>
                    {showNotifs && notifications.length > 0 && (
                        <div className="absolute bottom-8 left-0 w-72 bg-card border border-border/40 shadow-xl p-3 space-y-2 z-50">
                            {notifications.map((n) => (
                                <div key={n.id} className="text-xs text-gray-400 py-1 border-b border-border/20 last:border-0">{n.content}</div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 truncate">{user?.full_name || user?.name}</span>
                    <button onClick={() => { logout(); navigate("/"); }} className="text-gray-600 hover:text-white transition-colors" data-testid="logout-btn">
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </aside>
    );
};

const VERDICT_STYLES = {
    Retain:   { border: "border-emerald-500/30", bg: "bg-emerald-500/5",  text: "text-emerald-300" },
    Retrain:  { border: "border-amber-500/30",   bg: "bg-amber-500/5",    text: "text-amber-300" },
    Redeploy: { border: "border-red-500/30",     bg: "bg-red-500/5",      text: "text-red-300" },
};

const SEGMENT_STYLES = {
    "Core Builder":           { text: "text-emerald-300", border: "border-emerald-500/30", bg: "bg-emerald-500/5" },
    "Emerging Builder":       { text: "text-teal-300",    border: "border-teal-500/30",    bg: "bg-teal-500/5" },
    "Support Developer":      { text: "text-slate-300",   border: "border-slate-500/30",   bg: "bg-slate-500/5" },
    "Restructuring Candidate":{ text: "text-red-300",     border: "border-red-500/30",     bg: "bg-red-500/5" },
};

const BAND_ORDER = ["Low", "Moderate", "Significant Exposure", "High Exposure", "Transforming"];

const FLAG_LABELS = {
    SVP:    "Strategic Value Pattern",
    BI:     "Behavioural Isolation",
    CC:     "Coverage Concern",
    STP:    "Strategic Trajectory",
    "HOS+": "High-Order Specialisation",
    "MVO-L":"MV Over-weight (Low)",
    RoR:    "Right-of-Review filed",
};

export default function EnterpriseDashboard() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const picker = useCyclePicker();
    const { cycleId } = picker;
    const [data, setData] = useState(null);
    const [engagement, setEngagement] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cycleId) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/v2/dashboard/command-center/${cycleId}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { console.error(e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        api.get(`/v2/training/engagement/${cycleId}`)
            .then((r) => { if (!cancelled) setEngagement(r.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [api, cycleId]);

    const counts = data?.counts || {};
    const verdicts = data?.verdicts || {};
    const segments = data?.capability_segments || {};
    const bands = data?.displacement_bands || {};
    const flags = data?.flag_counts || {};
    const averages = data?.averages || {};

    return (
        <div className="flex">
            <EnterpriseSidebar current="/enterprise" />
            <main className="ml-56 flex-1 p-8 min-h-screen" data-testid="enterprise-dashboard">
                <div className="max-w-6xl space-y-8">
                    <div>
                        <h1 className="text-2xl font-display font-bold text-white">Command Center</h1>
                        <p className="text-sm text-gray-500 mt-1 font-body">
                            {data?.enterprise?.name || "Select a workspace"}{data?.cycle?.name ? ` · ${data.cycle.name}` : ""}
                        </p>
                    </div>

                    <CyclePickerBar picker={picker} />

                    {!cycleId && (
                        <div className="bg-card border border-border/40 p-8 text-center text-gray-500 text-sm">
                            No cycle selected. Create a cycle in the admin scoring module to light up this dashboard.
                        </div>
                    )}

                    {cycleId && loading && (
                        <div className="grid grid-cols-4 gap-4 animate-pulse">
                            {[1,2,3,4].map((i) => <div key={i} className="h-24 bg-card border border-border/40" />)}
                        </div>
                    )}

                    {cycleId && !loading && data && (
                        <>
                            {/* KPI cards */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {[
                                    { label: "Candidates",         value: counts.total_candidates ?? 0, icon: Users },
                                    { label: "Analysed",           value: counts.analysed ?? 0,         icon: ShieldCheck },
                                    { label: "Avg Capability",     value: (averages.capability ?? 0).toFixed(1), icon: TrendingUp },
                                    { label: "Avg Displacement",   value: (averages.displacement ?? 0).toFixed(1), icon: BarChart3 },
                                ].map((s, i) => (
                                    <motion.div key={s.label}
                                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                                        className="bg-card border border-border/40 p-5"
                                        data-testid={`kpi-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                                        <s.icon className="w-4 h-4 text-gray-600 mb-3" />
                                        <p className="text-2xl font-bold text-white font-mono">{s.value}</p>
                                        <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                                    </motion.div>
                                ))}
                            </div>

                            {engagement && engagement.candidates_with_recommendations > 0 && (
                                <div className="bg-card border border-border/40 p-5" data-testid="training-engagement-kpi">
                                    <div className="flex items-baseline justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-gray-500">Training engagement</p>
                                            <p className="text-[11px] text-gray-600 mt-0.5 font-body">Leading indicator of platform adoption — candidates who have expressed interest in suggested training.</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-3xl font-bold text-white font-mono">{engagement.engagement_pct.toFixed(1)}<span className="text-base text-gray-500">%</span></p>
                                            <p className="text-[11px] text-gray-500 font-mono">{engagement.candidates_engaged} of {engagement.candidates_with_recommendations} candidates</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Verdict distribution */}
                            <div className="bg-card border border-border/40 p-6" data-testid="verdict-distribution">
                                <h2 className="text-sm font-semibold text-white mb-4 font-body uppercase tracking-wider">Verdict Distribution</h2>
                                <div className="grid grid-cols-3 gap-4">
                                    {["Retain", "Retrain", "Redeploy"].map((v) => {
                                        const s = VERDICT_STYLES[v];
                                        const count = verdicts[v] || 0;
                                        return (
                                            <div key={v} className={`p-5 border ${s.border} ${s.bg}`} data-testid={`verdict-${v.toLowerCase()}-tile`}>
                                                <p className={`text-3xl font-bold ${s.text} font-mono`}>{count}</p>
                                                <p className={`text-xs mt-1 uppercase tracking-wider ${s.text}`}>{v}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Capability segments + Displacement bands */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                <div className="bg-card border border-border/40 p-6">
                                    <h3 className="text-sm font-semibold text-white mb-4 font-body uppercase tracking-wider">Capability Segments</h3>
                                    <div className="space-y-2">
                                        {Object.entries(SEGMENT_STYLES).map(([seg, s]) => {
                                            const n = segments[seg] || 0;
                                            return (
                                                <div key={seg} className={`flex items-center justify-between px-3 py-2 border ${s.border} ${s.bg}`}>
                                                    <span className={`text-sm ${s.text}`}>{seg}</span>
                                                    <span className={`text-sm font-mono font-bold ${s.text}`}>{n}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="bg-card border border-border/40 p-6">
                                    <h3 className="text-sm font-semibold text-white mb-4 font-body uppercase tracking-wider">Displacement Bands</h3>
                                    <div className="space-y-2">
                                        {BAND_ORDER.map((b) => {
                                            const n = bands[b] || 0;
                                            return (
                                                <div key={b} className="flex items-center justify-between px-3 py-2 border border-border/40 bg-black/30">
                                                    <span className="text-sm text-gray-300">{b}</span>
                                                    <span className="text-sm font-mono font-bold text-white">{n}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Strategic Pathway */}
                            <div className="bg-card border border-border/40 p-6" data-testid="strategic-pathway">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Strategic Pathway</p>
                                        <p className="text-lg text-white font-display">{data.strategic_pathway}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Core Builder Rate</p>
                                        <p className="text-2xl text-white font-mono font-bold">{data.core_builder_rate}%</p>
                                    </div>
                                </div>
                            </div>

                            {/* Top builders + Top risks */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                <div className="bg-card border border-border/40 p-6" data-testid="top-builders">
                                    <h3 className="text-sm font-semibold text-white mb-4 font-body uppercase tracking-wider">Top Builders</h3>
                                    {(data.top_builders || []).length === 0 && <p className="text-xs text-gray-600">No scored builders yet.</p>}
                                    <div className="space-y-2">
                                        {(data.top_builders || []).map((b) => (
                                            <button key={b.candidate_id}
                                                onClick={() => navigate(`/enterprise/worker/${b.candidate_id}`)}
                                                className="w-full flex items-center justify-between px-3 py-2 border border-border/40 hover:border-white/20 hover:bg-white/5 transition-colors text-left">
                                                <div>
                                                    <p className="text-sm text-white">{b.full_name}</p>
                                                    <p className="text-xs text-gray-500">{b.job_title} · {b.cadre}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-mono text-emerald-300">{(b.capability_composite ?? 0).toFixed(1)}</span>
                                                    <ArrowUpRight className="w-3 h-3 text-gray-600" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="bg-card border border-border/40 p-6" data-testid="top-risks">
                                    <h3 className="text-sm font-semibold text-white mb-4 font-body uppercase tracking-wider">Top Displacement Risks</h3>
                                    {(data.top_risks || []).length === 0 && <p className="text-xs text-gray-600">No verdicts yet.</p>}
                                    <div className="space-y-2">
                                        {(data.top_risks || []).map((r) => (
                                            <button key={r.candidate_id}
                                                onClick={() => navigate(`/enterprise/worker/${r.candidate_id}`)}
                                                className="w-full flex items-center justify-between px-3 py-2 border border-border/40 hover:border-white/20 hover:bg-white/5 transition-colors text-left">
                                                <div>
                                                    <p className="text-sm text-white">{r.full_name}</p>
                                                    <p className="text-xs text-gray-500">{r.job_title} · {r.displacement_band}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-mono text-red-300">{(r.displacement_composite ?? 0).toFixed(1)}</span>
                                                    <ArrowUpRight className="w-3 h-3 text-gray-600" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Advisory Flag rollup */}
                            <div className="bg-card border border-border/40 p-6" data-testid="flag-rollup">
                                <h3 className="text-sm font-semibold text-white mb-4 font-body uppercase tracking-wider">Advisory Flag Rollup</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                    {Object.keys(FLAG_LABELS).map((code) => {
                                        const n = flags[code] || 0;
                                        return (
                                            <div key={code} className={`p-3 border ${n > 0 ? "border-amber-500/30 bg-amber-500/5" : "border-border/40 bg-black/30"}`}>
                                                <p className={`text-xs font-mono ${n > 0 ? "text-amber-300" : "text-gray-500"}`}>{code}</p>
                                                <p className={`text-xl font-bold font-mono ${n > 0 ? "text-amber-300" : "text-gray-600"}`}>{n}</p>
                                                <p className="text-xs text-gray-600 mt-1">{FLAG_LABELS[code]}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
